import React, { type ComponentPropsWithoutRef, type ElementType } from 'react';

import './ExternalLink.scss';

type ExternalLinkProps<T extends ElementType = 'a'> = {
    /**
     * Element or component to render as. Defaults to a bare `<a>`. Pass a reactstrap `NavLink` /
     * `Button` (or any anchor-rendering wrapper) to give a nav/block link the same forced
     * `target="_blank"` + `rel` and the single cue, without hand-rolling them at the call site.
     * This shadows the wrapper's own `tag` prop, so its inner element can't be re-targeted — fine
     * here because the required `href` makes those wrappers render an anchor regardless.
     */
    tag?: T;
    /** Destination URL. Required — an ExternalLink without a destination is not a link. */
    href: string;
    /** Link content. Required — a link needs a visible label, not just the sr-only cue. */
    children: React.ReactNode;
    /** Defaults to "noopener". Use "noreferrer" for third-party sites to avoid leaking the referrer. */
    rel?: 'noopener' | 'noreferrer';
    /**
     * Render the visible `ion-md-open` glyph after the label. For nav/block links; leave off for
     * inline prose, where a mid-sentence glyph disrupts the copy. The glyph is decorative — the
     * appended cue below is the single accessible "opens in new window" indicator.
     */
    showIcon?: boolean;
} & Omit<ComponentPropsWithoutRef<T>, 'tag' | 'href' | 'target' | 'rel' | 'children'>;

/**
 * Link that opens in a new browser tab. Enforces `target="_blank"` + `rel="noopener"` (which
 * Biome's noBlankTarget rule cannot enforce on component wrappers) and appends a single,
 * consistent "opens in new window" screen-reader cue.
 */
export default function ExternalLink<T extends ElementType = 'a'>({
    tag,
    rel = 'noopener',
    showIcon = false,
    children,
    ...rest
}: ExternalLinkProps<T>) {
    const Tag: ElementType = tag ?? 'a';
    return (
        <Tag {...rest} target="_blank" rel={rel}>
            {children}
            {showIcon && <i className="icon ion-md-open external-link-icon" aria-hidden="true" />}
            <span className="visually-hidden"> (opens in new window)</span>
        </Tag>
    );
}
